import { Injectable } from '@angular/core';
import { ActiveCartService } from '@cart/data-access';
import { ProductListFacade } from '@catalog/data-access';
import { EnrichedComparisonEntry } from '@comparison/const';
import { ComparisonService } from '@comparison/feature';
import { ReferencesCmsComponents } from '@core/const';
import { CustomProduct } from '@product/const';
import { OrderEntry } from '@spartacus/core';
import { CurrentProductService } from '@spartacus/storefront';
import { WishlistEntry } from '@wishlist/const';
import { WishlistService } from '@wishlist/feature';
import { Observable, of, throwError } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { RetailRocketProductReferenceService } from './retail-rocket-product-reference.service';

@Injectable({ providedIn: 'root' })
export class RetailRocketCarouselFacade {
  constructor(
    private retailRocketProductReferenceService: RetailRocketProductReferenceService,
    private activeCartService: ActiveCartService,
    private productListFacade: ProductListFacade,
    private wishlistService: WishlistService,
    private comparisonService: ComparisonService,
    private currentProductService: CurrentProductService,
  ) {}

  // Роут полученя рекомендаций по списку айди товара и алгоритму Ретейлрокета
  // https://docs.retailrocket.ru/reference/recommendation-general-concepts

  loadRecomendations(uid: string, type: ReferencesCmsComponents, algorithms: string): void {
    this.getProductCodesByType(type)
      .pipe(take(1))
      .subscribe((productCodes) => {
        this.retailRocketProductReferenceService.loadRetailRocketRecomendations(uid, productCodes, algorithms);
      });
  }

  getRecomendations(uid: string, maximumNumberProducts: number): Observable<Observable<CustomProduct>[]> {
    return this.retailRocketProductReferenceService.getRetailRocketRecomendations(uid, maximumNumberProducts);
  }

  getRecomendationsLoading(uid: string): Observable<boolean> {
    return this.retailRocketProductReferenceService.getRetailRocketRecomendationsLoading(uid);
  }

  getProductCodesByType(type: ReferencesCmsComponents): Observable<string[]> {
    switch (type) {
      case ReferencesCmsComponents.RetailRocketProductReferencesComponent:
        return this.currentProductService.getProduct().pipe(
          take(1),
          filter(Boolean),
          map((product: CustomProduct) => [product.code]),
        );
      case ReferencesCmsComponents.RetailRocketCartReferencesComponent:
        return this.activeCartService.getEntries().pipe(
          take(1),
          filter(Boolean),
          map((entries: OrderEntry[]) => entries.map((entry) => entry.product.code)),
        );
      case ReferencesCmsComponents.RetailRocketCatalogReferencesComponent:
        return this.productListFacade.getProducts().pipe(
          filter((list: CustomProduct[]) => list !== undefined),
          take(1),
          map((entries: CustomProduct[]) => entries?.slice(0, 20).map((product) => product.code) || []),
        );
      case ReferencesCmsComponents.RetailRocketWishListReferencesComponent:
        return this.wishlistService.getEntries().pipe(
          filter((entries: WishlistEntry[]) => entries?.length > 0),
          take(1),
          map((entries: WishlistEntry[]) => entries.slice(0, 20).map((entry) => entry.productCode)),
        );
      case ReferencesCmsComponents.RetailRocketComparisonReferencesComponent:
        return this.comparisonService.activeCategoryEntries$.pipe(
          filter((entries: EnrichedComparisonEntry[]) => entries?.length > 0),
          map((entries) => entries.slice(0, 20).map((entry) => entry.product.code)),
        );
      case ReferencesCmsComponents.RetailRocketUserReferencesComponent:
        return of([]);
      default:
        return throwError(() => new Error(`Неизвестный cms-компонент: ${type}`));
    }
  }

  resetRecomendations(uid: string): void {
    this.retailRocketProductReferenceService.resetRetailRocketRecomendationList(uid);
  }
}
